import { useEffect, useRef, useState } from 'react'
import { ErrorBanner } from '@/components/AppLayout'
import { Modal } from '@/components/Modal'
import { attendeeInputSchema } from '@/lib/validation/schemas'
import { updateAttendee } from '@/lib/queries'
import type { Attendee } from '@/types/database'

interface Props {
  attendee: Attendee
  onSaved: (attendee: Attendee) => void
  onCancel: () => void
}

/** Names the dialog for screen readers; only ever one of these is open. */
const TITLE_ID = 'attendee-editor-title'

const FIELD_LABELS: Record<string, string> = {
  first_name: 'first name',
  last_name: 'last name',
  email: 'email',
  phone: 'phone',
  gender: 'gender',
  matchmaking_id: 'number',
}

export function AttendeeEditor({ attendee, onSaved, onCancel }: Props) {
  const [form, setForm] = useState({
    matchmakingId: attendee.matchmaking_id,
    firstName: attendee.first_name,
    lastName: attendee.last_name ?? '',
    email: attendee.email ?? '',
    phone: attendee.phone ?? '',
    gender: attendee.gender ?? '',
  })
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({})

  const firstFieldRef = useRef<HTMLInputElement>(null)

  // showModal() focuses the first thing it can find, which is the Close button
  // in the header — an odd place to land when the point of opening this is to
  // change a value. Start on the first field instead.
  useEffect(() => {
    firstFieldRef.current?.focus()
  }, [])

  async function handleSave() {
    setError(null)
    setFieldErrors({})

    const parsed = attendeeInputSchema.safeParse({
      firstName: form.firstName,
      lastName: form.lastName === '' ? null : form.lastName,
      email: form.email === '' ? null : form.email,
      phone: form.phone === '' ? null : form.phone,
      gender: form.gender === '' ? null : form.gender,
      matchmakingId: form.matchmakingId,
      isVisible: attendee.is_visible,
    })

    if (!parsed.success) {
      const errors: Record<string, string> = {}
      for (const issue of parsed.error.issues) {
        const key = issue.path[0]
        if (typeof key === 'string' && !(key in errors)) errors[key] = issue.message
      }
      setFieldErrors(errors)
      return
    }

    setSaving(true)
    try {
      // The database records which of these fields changed, so a later
      // Eventbrite re-import leaves them alone.
      const saved = await updateAttendee(attendee.id, {
        matchmaking_id: parsed.data.matchmakingId ?? attendee.matchmaking_id,
        first_name: parsed.data.firstName,
        last_name: parsed.data.lastName,
        email: parsed.data.email,
        phone: parsed.data.phone,
        gender: parsed.data.gender,
      })
      onSaved(saved)
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not save this attendee')
    } finally {
      setSaving(false)
    }
  }

  const locked = attendee.locked_fields

  return (
    <Modal labelledBy={TITLE_ID} onClose={onCancel}>
      <form
        className="card modal-panel"
        onSubmit={(e) => {
          e.preventDefault()
          void handleSave()
        }}
      >
        <div className="card-header">
          <h2 id={TITLE_ID}>Editing {attendee.first_name}</h2>
          <button className="btn btn-sm btn-ghost" type="button" onClick={onCancel}>
            Close
          </button>
        </div>

        {locked.length > 0 && (
          <p className="banner">
            Edited by hand: {locked.map((f) => FIELD_LABELS[f] ?? f).join(', ')}. Re-importing from
            Eventbrite will not overwrite {locked.length === 1 ? 'it' : 'them'}.
          </p>
        )}

        <div className="field">
          <label className="field-label" htmlFor="edit-number">
            Nametag number
          </label>
          <input
            id="edit-number"
            ref={firstFieldRef}
            type="text"
            inputMode="numeric"
            maxLength={3}
            value={form.matchmakingId}
            onChange={(e) => {
              setForm((f) => ({ ...f, matchmakingId: e.target.value.replace(/[^0-9]/g, '') }))
            }}
          />
          {fieldErrors.matchmakingId !== undefined && (
            <p className="field-error">{fieldErrors.matchmakingId}</p>
          )}
        </div>

        <div className="field">
          <label className="field-label" htmlFor="edit-first">
            First name
          </label>
          <input
            id="edit-first"
            type="text"
            value={form.firstName}
            onChange={(e) => {
              setForm((f) => ({ ...f, firstName: e.target.value }))
            }}
            required
          />
          {fieldErrors.firstName !== undefined && (
            <p className="field-error">{fieldErrors.firstName}</p>
          )}
        </div>

        <div className="field">
          <label className="field-label" htmlFor="edit-last">
            Last name
          </label>
          <input
            id="edit-last"
            type="text"
            value={form.lastName}
            onChange={(e) => {
              setForm((f) => ({ ...f, lastName: e.target.value }))
            }}
          />
        </div>

        <div className="field">
          <label className="field-label" htmlFor="edit-email">
            Email
          </label>
          <input
            id="edit-email"
            type="email"
            value={form.email}
            onChange={(e) => {
              setForm((f) => ({ ...f, email: e.target.value }))
            }}
          />
          {fieldErrors.email !== undefined && <p className="field-error">{fieldErrors.email}</p>}
        </div>

        <div className="field">
          <label className="field-label" htmlFor="edit-phone">
            Phone
          </label>
          <input
            id="edit-phone"
            type="tel"
            value={form.phone}
            onChange={(e) => {
              setForm((f) => ({ ...f, phone: e.target.value }))
            }}
          />
          {fieldErrors.phone !== undefined && <p className="field-error">{fieldErrors.phone}</p>}
        </div>

        <div className="field">
          <label className="field-label" htmlFor="edit-gender">
            Gender
          </label>
          <p className="field-hint">
            Free text. Used to group the &ldquo;most picked&rdquo; report.
          </p>
          <input
            id="edit-gender"
            type="text"
            value={form.gender}
            onChange={(e) => {
              setForm((f) => ({ ...f, gender: e.target.value }))
            }}
          />
        </div>

        {error !== null && <ErrorBanner message={error} />}

        <div className="row btn-group-stack">
          <button className="btn btn-primary" type="submit" disabled={saving}>
            {saving ? 'Saving…' : 'Save'}
          </button>
          <button className="btn" type="button" onClick={onCancel}>
            Cancel
          </button>
        </div>
      </form>
    </Modal>
  )
}
